import { bbox } from '@turf/turf';
import type * as MapGL from 'react-map-gl/mapbox';
import { proxy } from 'valtio';
import { updateIntelligenceStatus as apiUpdateIntelligenceStatus } from '~/api/projects2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { IntelligenceSummary, Project } from '~/common/types';
import { Notify } from '~/components2/Notify';
import {
  IntelligenceSectionTitle,
  type IntelligenceStatus,
  type IntelligenceV2,
  type TIntelligenceQuery,
} from './common';

type F = { [key: string]: GeoJSON.Feature };

class Store {
  isLoading: boolean = false;
  isError: boolean = false;

  projectIntelligence: { [key: string]: IntelligenceV2 } = {};
  config: { [key: string]: { [key: string]: GeoJSON.Feature } } = {};

  citygrid: F = {};

  hoveredFeature: GeoJSON.Feature | null = null;
  selectedFeature: GeoJSON.Feature | null = null;
  project?: Project | undefined = undefined;
  summary?: IntelligenceSummary | undefined = undefined;

  activeSection: IntelligenceSectionTitle = IntelligenceSectionTitle.NearbyProjects;

  map?: MapGL.MapRef = undefined;

  async formatData(projectId: string, data: TIntelligenceQuery) {
    try {
      this.isLoading = true;

      if (!data || !data.summary || !data.layerConfig || !data.projectIntelligence || !data.citygrid) {
        throw new Error('Missing data in response');
      }

      this.summary = data.summary;

      data.projectIntelligence.forEach((intelligence) => {
        this.projectIntelligence[intelligence.id] = intelligence;
      });

      Object.keys(data.layerConfig).forEach((layerId) => {
        const fc = data.layerConfig?.[layerId];
        if (!this.config[layerId]) {
          this.config[layerId] = {};
        }
        if (fc) {
          fc.features.forEach((f) => {
            const featureId = f.id?.toString();

            if (featureId) {
              if (!this.config[layerId]) {
                this.config[layerId] = {};
              }

              this.config[layerId][featureId] = f;
            }
          });
        }
      });

      data.citygrid.forEach((f) => {
        const id = f.properties?.id?.toString();
        if (id) {
          this.citygrid[id] = f;
        }
      });
    } catch (e) {
      console.error('Error getting intelligence: ', e);
      this.isError = true;
    } finally {
      this.isLoading = false;
    }
  }

  setProject(project: Project) {
    this.project = project;
  }

  setHoveredFeature(feature: GeoJSON.Feature) {
    this.hoveredFeature = feature;
  }

  resetHoveredFeature() {
    this.hoveredFeature = null;
  }

  setSelectedFeature(feature: GeoJSON.Feature) {
    if (this.hoveredFeature) {
      this.hoveredFeature = null;
    }

    this.selectedFeature = feature;
  }

  resetSelectedFeature() {
    this.selectedFeature = null;
  }

  private resetFields() {
    this.isLoading = true;
    this.isError = false;
    this.projectIntelligence = {};
    this.config = {};
    this.citygrid = {};
    this.project = undefined;
    this.summary = undefined;
    this.hoveredFeature = null;
    this.selectedFeature = null;
    this.activeSection = IntelligenceSectionTitle.NearbyProjects;
  }

  setup() {
    this.resetFields();
  }

  reset() {
    this.resetFields();
  }

  setCurrentMap(map?: MapGL.MapRef) {
    this.map = map;
  }

  resetCurrentMap() {
    this.map = undefined;
  }

  zoomToFeature(f: GeoJSON.Feature) {
    if (this.map) {
      const box = bbox(f) as [number, number, number, number];
      this.map.fitBounds(box, { padding: 100, speed: 2 });
    }
  }

  async updateFeatureStatus(reviewStatus: IntelligenceStatus, featureId?: string, metadata?: { [key: string]: any }) {
    try {
      this.resetHoveredFeature();
      const projectId = this.project?.id;

      if (!projectId || !featureId || !this.project) {
        throw new Error('Invalid Project ID or Feature ID');
      }

      const intel = this.projectIntelligence[featureId];

      if (!intel) {
        throw new Error('No matching intelligence item found!');
      }

      intel.reviewStatus = reviewStatus;

      const res = await apiUpdateIntelligenceStatus(projectId, featureId, reviewStatus);

      if (res.success) {
        Notify.info({
          title: 'Intelligence Review Status',
          message: 'Successfully updated Project Intelligence status.',
        });

        capturePosthogEvent(POSTHOG_EVENTS.intelligenceStatusUpdated, {
          project_id: projectId,
          organization_id: this.project.organization,
          feature_id: featureId,
          review_status: reviewStatus,
          layer_id: intel.layerId,
          ...metadata,
        });
      }
    } catch {
      Notify.error({
        title: 'Intelligence Review Status',
        message: 'There was an error updating your Intelligence Status, please contact support.',
      });
    }
  }
}

const store = proxy(new Store());
export default store;
